/**
 * Dettaglio ordine di acquisto con righe e azioni contestuali.
 * Azioni variano in base allo stato dell'ordine.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card } from '@floraltech/ui';
import { useFormatters } from '@lib/formatters';
import { StatoOrdineBadge } from '../components/StatoOrdineBadge';
import { StatoRicezioneBadge } from '../components/StatoRicezioneBadge';

interface Riga {
  id: string;
  ordine_id: string;
  articolo_id: string;
  codice_articolo: string;
  descrizione: string;
  unita_misura: string;
  quantita_ordinata: string;
  prezzo_unitario: string;
  importo_riga: string;
  note: string | null;
}

interface OrdineDetail {
  id: string;
  tenant_id: string;
  fornitore_id: string;
  rappresentante_id: string | null;
  preventivo_id: string | null;
  canale: string;
  acquirente_id: string | null;
  numero: string;
  anno: number;
  stato: string;
  data_ordine: string;
  data_consegna_attesa: string | null;
  riferimento_esterno_fornitore: string | null;
  conferma_fornitore_at: string | null;
  corriere: string | null;
  tracking_code: string | null;
  data_spedizione: string | null;
  data_consegna_effettiva: string | null;
  valuta: string;
  importo_totale: string;
  note: string | null;
  created_at: string;
  updated_at: string;
  righe: Riga[];
}

interface Ricezione {
  id: string;
  ordine_id: string;
  anno: number;
  numero_ddt: string | null;
  data_ricezione: string;
  stato: string;
  operatore_id: string;
  note_operatore: string | null;
  created_at: string;
}

export function OrdineDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currency, date, qty } = useFormatters();
  const token = localStorage.getItem('token');

  const [ordine, setOrdine] = useState<OrdineDetail | null>(null);
  const [ricezioni, setRicezioni] = useState<Ricezione[]>([]);
  const [fornitoreNome, setFornitoreNome] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showInvioModal, setShowInvioModal] = useState(false);
  const [emailDest, setEmailDest] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    const [ordRes, ricRes] = await Promise.all([
      fetch(`/api/v1/ordini/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
      fetch(`/api/v1/ordini/${id}/ricezioni`, { headers: { Authorization: `Bearer ${token}` } }),
    ]);
    if (ordRes.ok) {
      const data = await ordRes.json();
      setOrdine(data);
      if (data.fornitore_id) {
        const fornRes = await fetch(`/api/v1/soggetti/${data.fornitore_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (fornRes.ok) {
          const f = await fornRes.json();
          setFornitoreNome(f.ragione_sociale || [f.nome, f.cognome].filter(Boolean).join(' ') || null);
        }
      }
    }
    if (ricRes.ok) setRicezioni(await ricRes.json());
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [id, token]);

  const handleConferma = async () => {
    setActionLoading(true);
    const res = await fetch(`/api/v1/ordini/${id}/conferma`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) await fetchData();
    setActionLoading(false);
  };

  const handleInvia = async () => {
    if (!emailDest) return;
    setActionLoading(true);
    const res = await fetch(`/api/v1/ordini/${id}/invia`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ destinatario_email: emailDest }),
    });
    if (res.ok) {
      setShowInvioModal(false);
      await fetchData();
    }
    setActionLoading(false);
  };

  const handleAnnulla = async () => {
    setActionLoading(true);
    const res = await fetch(`/api/v1/ordini/${id}/annulla`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) await fetchData();
    setActionLoading(false);
  };

  const handleScaricaPdf = () => {
    window.open(`/api/v1/ordini/${id}/pdf?token=${token}`, '_blank');
  };

  const handleNuovaRicezione = async () => {
    setActionLoading(true);
    const res = await fetch(`/api/v1/ordini/${id}/ricezioni`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    });
    if (res.ok) {
      const ric = await res.json();
      navigate(`/ordini/ricezioni/${ric.id}`);
    }
    setActionLoading(false);
  };

  const handleConfermaFornitore = async () => {
    setActionLoading(true);
    const res = await fetch(`/api/v1/ordini/${id}/conferma-fornitore`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    });
    if (res.ok) await fetchData();
    setActionLoading(false);
  };

  const handleSpedizione = async () => {
    setActionLoading(true);
    const res = await fetch(`/api/v1/ordini/${id}/spedizione`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    });
    if (res.ok) await fetchData();
    setActionLoading(false);
  };

  const handleConsegna = async () => {
    setActionLoading(true);
    const res = await fetch(`/api/v1/ordini/${id}/consegnato`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    });
    if (res.ok) await fetchData();
    setActionLoading(false);
  };

  if (loading || !ordine) {
    return (
      <div className="p-6 text-muted-foreground">Caricamento…</div>
    );
  }

  return (
    <div className="p-6 space-y-6 w-full">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-outfit font-semibold text-foreground">
            Ordine {ordine.numero}
          </h1>
          <div className="flex items-center gap-3 mt-1">
            <StatoOrdineBadge stato={ordine.stato} />
            <span className="text-sm text-muted-foreground">
              {date(ordine.data_ordine)}
            </span>
          </div>
        </div>

        {/* Azioni contestuali */}
        <div className="flex gap-2">
          {ordine.stato !== 'bozza' && ordine.stato !== 'annullato' && (
            <Button variant="outline" onClick={handleScaricaPdf}>
              Scarica PDF
            </Button>
          )}

          {ordine.stato === 'bozza' && (
            <>
              <Button variant="outline" onClick={() => navigate(`/ordini/${id}/modifica`)}>
                Modifica
              </Button>
              <Button onClick={handleConferma} disabled={actionLoading}>
                Conferma
              </Button>
              <Button variant="destructive" onClick={handleAnnulla} disabled={actionLoading}>
                Annulla
              </Button>
            </>
          )}

          {ordine.stato === 'confermato' && (
            <>
              <Button onClick={() => setShowInvioModal(true)} disabled={actionLoading}>
                Invia al fornitore
              </Button>
              <Button variant="destructive" onClick={handleAnnulla} disabled={actionLoading}>
                Annulla
              </Button>
            </>
          )}

          {ordine.stato === 'inviato' && (
            <Button variant="outline" onClick={handleConfermaFornitore} disabled={actionLoading}>
              Conferma fornitore
            </Button>
          )}

          {(ordine.stato === 'inviato' || ordine.stato === 'confermato_fornitore') && (
            <Button variant="outline" onClick={handleSpedizione} disabled={actionLoading}>
              In spedizione
            </Button>
          )}

          {ordine.stato === 'in_spedizione' && (
            <Button variant="outline" onClick={handleConsegna} disabled={actionLoading}>
              Consegnato
            </Button>
          )}

          {(['inviato', 'confermato_fornitore', 'in_spedizione', 'consegnato', 'in_ricezione'].includes(ordine.stato)) && (
            <Button onClick={handleNuovaRicezione} disabled={actionLoading}>
              Nuova ricezione
            </Button>
          )}
        </div>
      </div>

      {/* Info ordine */}
      <Card className="p-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Fornitore:</span>{' '}
            <span className="font-semibold">{fornitoreNome || '—'}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Canale:</span>{' '}
            <span className="capitalize">{ordine.canale}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Consegna attesa:</span>{' '}
            {ordine.data_consegna_attesa ? date(ordine.data_consegna_attesa) : '—'}
          </div>
          {ordine.riferimento_esterno_fornitore && (
            <div>
              <span className="text-muted-foreground">Rif. fornitore:</span>{' '}
              {ordine.riferimento_esterno_fornitore}
            </div>
          )}
          {ordine.conferma_fornitore_at && (
            <div>
              <span className="text-muted-foreground">Confermato forn.:</span>{' '}
              {date(ordine.conferma_fornitore_at)}
            </div>
          )}
          {ordine.corriere && (
            <div>
              <span className="text-muted-foreground">Corriere:</span> {ordine.corriere}
            </div>
          )}
          {ordine.tracking_code && (
            <div>
              <span className="text-muted-foreground">Tracking:</span> {ordine.tracking_code}
            </div>
          )}
          {ordine.data_spedizione && (
            <div>
              <span className="text-muted-foreground">Spedito il:</span>{' '}
              {date(ordine.data_spedizione)}
            </div>
          )}
          {ordine.data_consegna_effettiva && (
            <div>
              <span className="text-muted-foreground">Consegnato il:</span>{' '}
              {date(ordine.data_consegna_effettiva)}
            </div>
          )}
          {ordine.note && (
            <div className="col-span-2">
              <span className="text-muted-foreground">Note:</span> {ordine.note}
            </div>
          )}
        </div>
      </Card>

      {/* Righe */}
      <Card className="overflow-hidden">
        <div className="px-4 py-2 border-b border-border bg-muted/50">
          <h2 className="text-sm font-semibold text-foreground">Righe ordine</h2>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left px-4 py-2 text-muted-foreground font-medium">Codice</th>
              <th className="text-left px-4 py-2 text-muted-foreground font-medium">Descrizione</th>
              <th className="text-left px-4 py-2 text-muted-foreground font-medium">U.M.</th>
              <th className="text-right px-4 py-2 text-muted-foreground font-medium">Q.tà</th>
              <th className="text-right px-4 py-2 text-muted-foreground font-medium">Prezzo</th>
              <th className="text-right px-4 py-2 text-muted-foreground font-medium">Importo</th>
            </tr>
          </thead>
          <tbody>
            {ordine.righe.map((r) => (
              <tr key={r.id} className="border-b border-border">
                <td className="px-4 py-2">{r.codice_articolo}</td>
                <td className="px-4 py-2">{r.descrizione}</td>
                <td className="px-4 py-2">{r.unita_misura}</td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {qty(r.quantita_ordinata)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {currency(r.prezzo_unitario)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {currency(r.importo_riga)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-primary">
              <td colSpan={5} className="px-4 py-2 text-right font-semibold">Totale</td>
              <td className="px-4 py-2 text-right font-semibold tabular-nums">
                {currency(ordine.importo_totale)}
              </td>
            </tr>
          </tfoot>
        </table>
      </Card>

      {/* Ricezioni */}
      {ricezioni.length > 0 && (
        <Card className="overflow-hidden">
          <div className="px-4 py-2 border-b border-border bg-muted/50">
            <h2 className="text-sm font-semibold text-foreground">Ricezioni</h2>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-4 py-2 text-muted-foreground font-medium">Data</th>
                <th className="text-left px-4 py-2 text-muted-foreground font-medium">DDT</th>
                <th className="text-left px-4 py-2 text-muted-foreground font-medium">Stato</th>
                <th className="text-left px-4 py-2 text-muted-foreground font-medium">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {ricezioni.map((r) => (
                <tr key={r.id} className="border-b border-border">
                  <td className="px-4 py-2">{date(r.data_ricezione)}</td>
                  <td className="px-4 py-2">{r.numero_ddt || '—'}</td>
                  <td className="px-4 py-2">
                    <StatoRicezioneBadge stato={r.stato} />
                  </td>
                  <td className="px-4 py-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/ordini/ricezioni/${r.id}`)}
                    >
                      Dettaglio
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Modale invio */}
      {showInvioModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="p-6 w-96 space-y-4">
            <h3 className="text-lg font-outfit font-semibold text-foreground">
              Invia ordine al fornitore
            </h3>
            <div>
              <label className="text-sm text-muted-foreground">Email destinatario</label>
              <input
                type="email"
                value={emailDest}
                onChange={(e) => setEmailDest(e.target.value)}
                className="w-full mt-1 rounded border border-border bg-card text-foreground px-3 py-2 text-sm"
                placeholder="fornitore@email.com"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowInvioModal(false)}>
                Annulla
              </Button>
              <Button onClick={handleInvia} disabled={!emailDest || actionLoading}>
                {actionLoading ? 'Invio…' : 'Invia'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
